import { Button, Card, Flex, Icon, Text } from '@auviknetworks/iceberg-ui';
import { Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { INTEGRATION_OPTION_GOOGLE_AUTOMATED } from 'application/Shared/Components/Client/Components/IntegrationSelector';
import { FontSizes, Sizes } from 'application/Shared/Constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

export const OktaIntegrationBanner = ({ integrationType, clientId }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { partnerId } = useParams();
    const params = new URLSearchParams({ collapse: 'saasOpsIntegrations' });
    if (clientId) {
        params.append('clientId', clientId);
    }
    const configureNowUrl = `/partner/${partnerId}/integrations/clientLevel?${params.toString()}`;

    return (
        <Card marginTop="32px" padding="16px" width="100%">
            <Flex
                direction="column"
                gap="16px"
                sx={{ display: 'flex', flexDirection: 'column' }}
            >
                <Flex
                    direction="row"
                    alignItems="center"
                    sx={{ display: 'flex', flexDirection: 'row' }}
                >
                    <Icon
                        src="Okta"
                        size={Sizes[4]}
                        className="icon-spin"
                        sx={{
                            color: theme.palette.text.secondary
                        }}
                    />
                    <Text
                        fontSize={2}
                        color={theme.palette.text.secondary}
                        marginLeft={FontSizes[3]}
                    >
                        {t('SuccessfulIntegrationSetup.oktaBanner.text', {
                            integrationType:
                                integrationType ===
                                INTEGRATION_OPTION_GOOGLE_AUTOMATED
                                    ? 'Google Workspace'
                                    : 'Microsoft Entra ID'
                        })}
                    </Text>
                </Flex>
                <Flex
                    justifyContent="flex-end"
                    marginTop="16px"
                    sx={{ display: 'flex', justifyContent: 'flex-end' }}
                >
                    <Button
                        data-trackid="oktaIntegrationBannerConfigureButton"
                        data-testid="oktaIntegrationBannerConfigureButton"
                        variant="inverted"
                        onClick={() => {
                            navigate(configureNowUrl);
                        }}
                    >
                        {t('SuccessfulIntegrationSetup.oktaBanner.button')}
                    </Button>
                </Flex>
            </Flex>
        </Card>
    );
};

export const OnboardingOktaIntegrationBanner = ({
    integrationType,
    clientId
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { partnerId } = useParams();
    const params = new URLSearchParams({
        clientId,
        collapse: 'saasOpsIntegrations'
    });
    const configureNowUrl = `/partner/${partnerId}/integrations/clientLevel?${params.toString()}`;

    const integrationTypeDisplay =
        integrationType === INTEGRATION_OPTION_GOOGLE_AUTOMATED
            ? 'Google Workspace'
            : 'Microsoft Entra ID';

    return (
        <Flex
            alignItems="center"
            flexDirection="column"
            mt={5}
            mb={5}
            p={4}
            backgroundColor={theme.palette.onboarding.backgroundColorGray}
            width="100%"
            justifyContent="space-between"
            position="relative"
        >
            <Chip
                label={t('Onboarding.oktaEnhancement.bonusConfiguration')}
                size="small"
                variant="whiteText"
                sx={{
                    backgroundColor:
                        theme.palette.onboarding
                            .bonusConfigurationBadgeBackgroundColor,
                    fontWeight: '600',
                    left: '50%',
                    position: 'absolute',
                    top: '-12px',
                    transform: 'translateX(-50%)'
                }}
            />
            <Flex flexDirection="column" alignItems="center" mt={2}>
                <Icon
                    src="Okta"
                    size={FontSizes[5]}
                    className="icon-spin"
                    sx={{
                        color: theme.palette.text.secondary,
                        marginBottom: '24px',
                        marginTop: '24px'
                    }}
                />

                <Text
                    color={theme.palette.onboarding.cardBodyText}
                    fontSize={[3]}
                    mt={4}
                    textAlign="center"
                >
                    {t('Onboarding.oktaEnhancement.title')}
                </Text>

                <Text
                    fontSize={[0]}
                    color={theme.palette.onboarding.cardBodyText}
                    mb={3}
                    mt={2}
                    textAlign="center"
                >
                    {t('Onboarding.oktaEnhancement.subtitle')}
                </Text>

                <Text
                    fontSize={[1]}
                    color={theme.palette.onboarding.cardBodyText}
                    mb={3}
                >
                    {t('Onboarding.oktaEnhancement.description', {
                        integrationType: integrationTypeDisplay
                    })}
                </Text>

                <Text
                    fontSize={[0]}
                    color={theme.palette.text.secondary}
                    mb={3}
                >
                    {t('Onboarding.oktaEnhancement.disclaimer')}
                </Text>
            </Flex>

            <Button
                sx={{
                    marginTop: '32px'
                }}
                data-trackid="onboardingOktaIntegrationBannerConfigureButton"
                data-testid="onboardingOktaIntegrationBannerConfigureButton"
                variant="inverted"
                onClick={() => {
                    navigate(configureNowUrl);
                }}
            >
                {t('Onboarding.oktaEnhancement.button')}
            </Button>
        </Flex>
    );
};
